// One metadata entry per client/UI setting.
//
// The registry is the single source of truth for:
//   - the admin "Settings defaults" editor (search, categories, controls),
//   - per-setting visibility (none / player / tester / admin),
//   - reset buttons and effective-default resolution.
//
// Runtime values stay owned by `SettingsStore` and the systems that consume
// them; entries only describe where and how to read/write a value. Server
// rules/game modes are intentionally not here (they are server-authoritative
// and stay in the Rules / Game modes editors).

import { DEFAULTS, type Settings } from "../settings";
import { MODE_GROUPS } from "../modes";
import { RULE_GROUPS } from "../rules";
import { ACTION_GROUPS, ACTION_LABELS, DEFAULT_BINDS, displayCode } from "../binds";
import { SOUND_EVENTS } from "../audio";
import type { SettingAccess } from "./access";

export type SettingKind = "toggle" | "number" | "select" | "color" | "text" | "group" | "bind";

export interface SettingOption {
  value: string;
  label: string;
}

export interface SettingDef {
  /** Stable id used by the defaults document and the UI. */
  id: string;
  category: string[];
  label: string;
  help?: string;
  kind: SettingKind;
  /** Code baseline; "reset to shipped" targets this. */
  shipped: unknown;
  access: SettingAccess;
  /** Dot path into the `Settings` object. Absent for groups/runtime entries. */
  path?: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: SettingOption[];
  /** Runtime debug flag (Advanced page); applied through `applyDebug`. */
  runtimeKey?: string;
  /** Structured editor group: reset/open from the admin list. */
  group?: string;
  /** Extra search terms (synonyms, abbreviations, common misspellings). */
  keywords?: string[];
  /** Custom accessors for settings that are not a plain `Settings` path. */
  get?: (settings: unknown) => unknown;
  set?: (settings: unknown, value: unknown) => void;
  /** The page already has its own reset control for this setting. */
  inlineReset?: boolean;
  /** Path may be absent until the setting is first written (sound assignments). */
  optionalPath?: boolean;
}

const n = (
  id: string,
  category: string[],
  label: string,
  path: string,
  shipped: number,
  min: number,
  max: number,
  step: number,
  access: SettingAccess = "player",
  unit?: string,
  help?: string,
): SettingDef => ({
  id,
  category,
  label,
  kind: "number",
  shipped,
  access,
  path,
  min,
  max,
  step,
  unit,
  help,
});

const b = (
  id: string,
  category: string[],
  label: string,
  path: string,
  shipped: boolean,
  access: SettingAccess = "player",
  help?: string,
): SettingDef => ({ id, category, label, kind: "toggle", shipped, access, path, help });

const c = (
  id: string,
  category: string[],
  label: string,
  path: string,
  shipped: string,
  access: SettingAccess = "player",
): SettingDef => ({ id, category, label, kind: "color", shipped, access, path });

const sel = (
  id: string,
  category: string[],
  label: string,
  path: string,
  shipped: unknown,
  options: SettingOption[],
  access: SettingAccess = "player",
): SettingDef => ({ id, category, label, kind: "select", shipped, access, path, options });

const grp = (
  id: string,
  category: string[],
  label: string,
  path: string,
  shipped: unknown,
  group: string,
  help?: string,
): SettingDef => ({
  id,
  category,
  label,
  kind: "group",
  shipped,
  access: "player",
  path,
  group,
  help,
});

const TRAIL_MODES: SettingOption[] = [
  { value: "custom", label: "Custom" },
  { value: "random", label: "Random" },
  { value: "team", label: "Team" },
];

const HITMARKER_STYLES: SettingOption[] = [
  { value: "x", label: "X" },
  { value: "cross", label: "Cross" },
  { value: "dot", label: "Dot" },
];

const TEXTURE_MODES: SettingOption[] = [
  { value: "nearest", label: "Nearest" },
  { value: "bilinear", label: "Bilinear" },
  { value: "trilinear", label: "Trilinear" },
  { value: "anisotropic", label: "Trilinear + anisotropic" },
];

const DOWNSTREAM_RATES: SettingOption[] = [
  { value: "125", label: "125 Hz" },
  { value: "62.5", label: "62.5 Hz" },
  { value: "41.66", label: "41.66 Hz" },
  { value: "31.25", label: "31.25 Hz" },
];

const VIDEO = ["video"];
const EFFECTS = ["video", "effects"];
const HIT = ["video", "hit-feedback"];
const AUDIO = ["audio"];
const CONTROLS = ["controls"];
const HUD = ["hud"];
const WEAPONS = ["weapons"];
const ADVANCED = ["advanced"];
const DEBUG = ["advanced", "debug"];

const cgaz = DEFAULTS.hud.cgazStyle;
const fb = DEFAULTS.hitFeedback;
const ev = DEFAULTS.enemyVisual;

const CORE_SETTINGS: SettingDef[] = [
  // ---- General / video ---------------------------------------------------
  n("video.fov", VIDEO, "Field of view", "fov", DEFAULTS.fov, 60, 160, 1, "player", "°"),
  n("video.panini", VIDEO, "Panini strength", "panini", DEFAULTS.panini, 0, 3, 0.05),
  sel("video.textureMode", VIDEO, "Texture filtering", "textureMode", DEFAULTS.textureMode, TEXTURE_MODES),

  // ---- Video / effects ---------------------------------------------------
  n("video.effects.truelightning", EFFECTS, "Lightning beam follows crosshair", "cgTrueLightning", DEFAULTS.cgTrueLightning, 0, 1, 0.05),
  n("video.effects.trailSeconds", EFFECTS, "Player trail", "trailSeconds", DEFAULTS.trailSeconds, 0, 3, 0.1, "player", "s"),
  n("video.effects.trailWidth", EFFECTS, "Trail width", "trailWidth", DEFAULTS.trailWidth, 0.5, 8, 0.1),
  n("video.effects.trailIntensity", EFFECTS, "Trail glow", "trailIntensity", DEFAULTS.trailIntensity, 0.1, 2, 0.05),
  sel("video.effects.trailSelfMode", EFFECTS, "Your trail colour mode", "trailSelfMode", DEFAULTS.trailSelfMode, TRAIL_MODES),
  c("video.effects.trailSelfColor", EFFECTS, "Your trail colour", "trailSelfColor", DEFAULTS.trailSelfColor),
  c("video.effects.trailSelfTeamColor", EFFECTS, "Your team colour", "trailSelfTeamColor", DEFAULTS.trailSelfTeamColor),
  sel("video.effects.trailEnemyMode", EFFECTS, "Enemy trail colour mode", "trailEnemyMode", DEFAULTS.trailEnemyMode, TRAIL_MODES),
  c("video.effects.trailEnemyColor", EFFECTS, "Enemy trail colour", "trailEnemyColor", DEFAULTS.trailEnemyColor),
  c("video.effects.trailEnemyTeamColor", EFFECTS, "Enemy team colour", "trailEnemyTeamColor", DEFAULTS.trailEnemyTeamColor),
  n("video.enemy.outlineSize", EFFECTS, "Enemy outline size", "enemyVisual.outlineSize", ev.outlineSize, 0.5, 3, 0.05),
  n("video.enemy.outlineBrightness", EFFECTS, "Outline brightness", "enemyVisual.outlineBrightness", ev.outlineBrightness, 0, 2, 0.05),
  n("video.enemy.haloSize", EFFECTS, "Enemy halo size", "enemyVisual.haloSize", ev.haloSize, 0.5, 3, 0.05),
  n("video.enemy.haloBrightness", EFFECTS, "Halo brightness", "enemyVisual.haloBrightness", ev.haloBrightness, 0, 2, 0.05),
  c("video.vfx.explosion", EFFECTS, "Explosion colour", "vfxColors.explosion", DEFAULTS.vfxColors.explosion),
  c("video.vfx.sparks", EFFECTS, "Sparks colour", "vfxColors.sparks", DEFAULTS.vfxColors.sparks),
  c("video.vfx.muzzle", EFFECTS, "Muzzle colour", "vfxColors.muzzle", DEFAULTS.vfxColors.muzzle),
  c("video.vfx.smoke", EFFECTS, "Smoke colour", "vfxColors.smoke", DEFAULTS.vfxColors.smoke),

  // ---- Video / hit feedback ---------------------------------------------
  b("video.hit.hitmarker", HIT, "Hitmarker", "hitFeedback.hitmarker", fb.hitmarker),
  sel("video.hit.hitmarkerStyle", HIT, "Hitmarker style", "hitFeedback.hitmarkerStyle", fb.hitmarkerStyle, HITMARKER_STYLES),
  n("video.hit.hitmarkerSize", HIT, "Hitmarker size", "hitFeedback.hitmarkerSize", fb.hitmarkerSize, 6, 64, 1, "player", "px"),
  n("video.hit.hitmarkerThickness", HIT, "Hitmarker thickness", "hitFeedback.hitmarkerThickness", fb.hitmarkerThickness, 1, 8, 0.5, "player", "px"),
  c("video.hit.hitmarkerColor", HIT, "Hitmarker colour", "hitFeedback.hitmarkerColor", fb.hitmarkerColor),
  c("video.hit.hitmarkerKillColor", HIT, "Hitmarker kill colour", "hitFeedback.hitmarkerKillColor", fb.hitmarkerKillColor),
  n("video.hit.hitmarkerDuration", HIT, "Hitmarker duration", "hitFeedback.hitmarkerDuration", fb.hitmarkerDuration, 40, 600, 10, "player", "ms"),
  n("video.hit.hitmarkerOpacity", HIT, "Hitmarker opacity", "hitFeedback.hitmarkerOpacity", fb.hitmarkerOpacity, 0.1, 1, 0.05),
  b("video.hit.damageNumbers", HIT, "Damage numbers", "hitFeedback.damageNumbers", fb.damageNumbers),
  n("video.hit.damageNumberSize", HIT, "Damage number size", "hitFeedback.damageNumberSize", fb.damageNumberSize, 8, 64, 1, "player", "px"),
  c("video.hit.damageNumberColor", HIT, "Damage number colour", "hitFeedback.damageNumberColor", fb.damageNumberColor),
  c("video.hit.damageNumberKillColor", HIT, "Damage number kill colour", "hitFeedback.damageNumberKillColor", fb.damageNumberKillColor),
  n("video.hit.damageNumberDuration", HIT, "Damage number duration", "hitFeedback.damageNumberDuration", fb.damageNumberDuration, 200, 2000, 25, "player", "ms"),
  n("video.hit.damageNumberRise", HIT, "Damage number rise", "hitFeedback.damageNumberRise", fb.damageNumberRise, 0, 200, 2, "player", "px"),
  n("video.hit.damageNumberOpacity", HIT, "Damage number opacity", "hitFeedback.damageNumberOpacity", fb.damageNumberOpacity, 0.1, 1, 0.05),
  n("audio.hitSoundVolume", AUDIO, "Hit sound volume", "hitFeedback.hitSoundVolume", fb.hitSoundVolume, 0, 2, 0.05),

  // ---- Audio -------------------------------------------------------------
  n("audio.volume", AUDIO, "Master volume", "volume", DEFAULTS.volume, 0, 1, 0.01),

  // ---- Controls / weapons (groups) --------------------------------------
  n("controls.sensitivity", CONTROLS, "Sensitivity", "sensitivity", DEFAULTS.sensitivity, 0.1, 50, 0.01),
  grp("controls.binds", CONTROLS, "Key binds", "binds", DEFAULTS.binds, "binds", "Open the Controls page to edit individual keys."),
  n(
    "weapons.defaults.yawMultiplier",
    WEAPONS,
    "Yaw multiplier (default)",
    "weaponSettings.defaults.yawMultiplier",
    1,
    0.05,
    10,
    0.01,
    "player",
    "×",
    "Horizontal mouse-turn multiplier for every weapon unless that weapon overrides it.",
  ),
  n(
    "weapons.defaults.pitchMultiplier",
    WEAPONS,
    "Pitch multiplier (default)",
    "weaponSettings.defaults.pitchMultiplier",
    1,
    0.05,
    10,
    0.01,
    "player",
    "×",
    "Vertical mouse-look multiplier for every weapon unless that weapon overrides it.",
  ),
  grp("weapons.settings", WEAPONS, "Per-weapon settings", "weaponSettings", DEFAULTS.weaponSettings, "weapons", "Sensitivity, FOV, yaw/pitch multiplier, crosshair, sounds, effects and position per weapon."),
  grp("weapons.sounds", WEAPONS, "Sound assignments", "sounds", DEFAULTS.sounds, "sounds", "Assign files from snd/ to game events."),

  // ---- HUD ---------------------------------------------------------------
  n("hud.cgaz.opacity", HUD, "cgaz opacity", "hud.cgazStyle.opacity", cgaz.opacity, 0, 1, 0.05),
  n("hud.cgaz.height", HUD, "cgaz height", "hud.cgazStyle.height", cgaz.height, 2, 40, 1, "player", "px"),
  n("hud.cgaz.width", HUD, "cgaz width", "hud.cgazStyle.width", cgaz.width, 160, 1600, 20, "player", "px"),
  n("hud.cgaz.bgOpacity", HUD, "cgaz background", "hud.cgazStyle.bgOpacity", cgaz.bgOpacity, 0, 1, 0.05),
  b("hud.cgaz.shadow", HUD, "cgaz shadow", "hud.cgazStyle.shadow", cgaz.shadow),
  b("hud.cgaz.border", HUD, "cgaz border", "hud.cgazStyle.border", cgaz.border),
  n("hud.cgaz.borderWidth", HUD, "cgaz border width", "hud.cgazStyle.borderWidth", cgaz.borderWidth, 1, 6, 1, "player", "px"),
  c("hud.cgaz.borderColor", HUD, "cgaz border colour", "hud.cgazStyle.borderColor", cgaz.borderColor),
  n("hud.cgaz.borderOpacity", HUD, "cgaz border opacity", "hud.cgazStyle.borderOpacity", cgaz.borderOpacity, 0, 1, 0.05),
  c("hud.cgaz.colors.dead", HUD, "cgaz dead colour", "hud.cgazStyle.colors.dead", cgaz.colors.dead),
  c("hud.cgaz.colors.partial", HUD, "cgaz partial colour", "hud.cgazStyle.colors.partial", cgaz.colors.partial),
  c("hud.cgaz.colors.fast", HUD, "cgaz fast colour", "hud.cgazStyle.colors.fast", cgaz.colors.fast),
  c("hud.cgaz.colors.turn", HUD, "cgaz turn colour", "hud.cgazStyle.colors.turn", cgaz.colors.turn),
  c("hud.cgaz.colors.decel", HUD, "cgaz decel colour", "hud.cgazStyle.colors.decel", cgaz.colors.decel),
  n("hud.cgaz.alphas.dead", HUD, "cgaz dead alpha", "hud.cgazStyle.alphas.dead", cgaz.alphas.dead, 0, 1, 0.05),
  n("hud.cgaz.alphas.partial", HUD, "cgaz partial alpha", "hud.cgazStyle.alphas.partial", cgaz.alphas.partial, 0, 1, 0.05),
  n("hud.cgaz.alphas.fast", HUD, "cgaz fast alpha", "hud.cgazStyle.alphas.fast", cgaz.alphas.fast, 0, 1, 0.05),
  n("hud.cgaz.alphas.turn", HUD, "cgaz turn alpha", "hud.cgazStyle.alphas.turn", cgaz.alphas.turn, 0, 1, 0.05),
  n("hud.cgaz.alphas.decel", HUD, "cgaz decel alpha", "hud.cgazStyle.alphas.decel", cgaz.alphas.decel, 0, 1, 0.05),
  grp("hud.layout", HUD, "HUD layout", "hudLayout", DEFAULTS.hudLayout, "hud-layout", "Open the HUD editor to move, scale and hide elements."),
  grp("crosshair.store", WEAPONS, "Crosshair", "crosshair", DEFAULTS.crosshair, "crosshair", "Open the crosshair editor to edit layers and hit effects."),

  // ---- Advanced (runtime debug) -----------------------------------------
  sel("advanced.downstreamHz", ADVANCED, "Snapshot rate (downstream)", "downstreamHz", DEFAULTS.downstreamHz, DOWNSTREAM_RATES, "tester"),
  {
    id: "advanced.debug.wireframe",
    category: DEBUG,
    label: "Wireframe",
    kind: "toggle",
    shipped: false,
    access: "admin",
    runtimeKey: "wireframe",
  },
  {
    id: "advanced.debug.pvs",
    category: DEBUG,
    label: "PVS culling",
    kind: "toggle",
    shipped: true,
    access: "admin",
    runtimeKey: "pvs",
  },
  {
    id: "advanced.debug.frustum",
    category: DEBUG,
    label: "Frustum culling",
    kind: "toggle",
    shipped: true,
    access: "admin",
    runtimeKey: "frustum",
  },
  {
    id: "advanced.debug.rawmouse",
    category: DEBUG,
    label: "Raw input",
    kind: "toggle",
    shipped: true,
    access: "admin",
    runtimeKey: "rawmouse",
  },
  {
    id: "advanced.debug.vfxtrails",
    category: DEBUG,
    label: "VFX trails",
    kind: "toggle",
    shipped: true,
    access: "admin",
    runtimeKey: "vfxtrails",
  },
  {
    id: "advanced.debug.vfxcollide",
    category: DEBUG,
    label: "VFX collide",
    kind: "toggle",
    shipped: true,
    access: "admin",
    runtimeKey: "vfxcollide",
  },
  {
    id: "advanced.debug.bloom",
    category: DEBUG,
    label: "Bloom",
    kind: "toggle",
    shipped: true,
    access: "admin",
    runtimeKey: "bloom",
  },
  {
    id: "advanced.debug.netgraph",
    category: DEBUG,
    label: "Net graph",
    kind: "toggle",
    shipped: false,
    access: "admin",
    runtimeKey: "netgraph",
  },
  {
    id: "advanced.debug.profiler",
    category: DEBUG,
    label: "Profiler",
    kind: "toggle",
    shipped: false,
    access: "admin",
    runtimeKey: "profiler",
  },
  ...[
    ["interp", "Interp delay", 100, 20, 300, 1, "ms"],
    ["smooth", "Correction smoothing", 150, 0, 1000, 10, "ms"],
    ["lag", "Sim latency (one-way)", 0, 0, 1000, 5, "ms"],
    ["jitter", "Sim jitter", 0, 0, 500, 5, "ms"],
    ["loss", "Sim loss", 0, 0, 100, 1, "%"],
    ["fpscap", "FPS cap (0 = off)", 0, 0, 500, 1, "fps"],
    ["vfxamount", "VFX amount", 1, 0, 3, 0.1, "×"],
    ["smokedensity", "Smoke density", 0.5, 0, 3, 0.1, "×"],
    ["smokeopacity", "Smoke opacity", 0.1, 0, 1, 0.05, ""],
    ["vfxsize", "VFX size", 1, 0.2, 3, 0.1, "×"],
    ["bloomintensity", "Bloom intensity", 0.9, 0, 3, 0.05, ""],
    ["bloomthreshold", "Bloom threshold", 0.7, 0, 1, 0.05, ""],
  ].map(
    ([key, label, shipped, min, max, step, unit]): SettingDef => ({
      id: `advanced.debug.${key}`,
      category: DEBUG,
      label: String(label),
      kind: "number",
      shipped: Number(shipped),
      access: "tester",
      runtimeKey: String(key),
      min: Number(min),
      max: Number(max),
      step: Number(step),
      unit: String(unit),
    }),
  ),
];

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function bindCodes(binds: Record<string, string>, action: string): string[] {
  return Object.entries(binds)
    .filter(([, value]) => value === action)
    .map(([code]) => code)
    .sort();
}

function bindRaw(value: unknown): string[] {
  return typeof value === "string" ? value.trim().split(/\s+/).filter(Boolean) : [];
}

function setBindAction(settings: Settings, action: string, value: unknown): void {
  if (!settings.binds) return;
  for (const [code, bound] of Object.entries(settings.binds)) {
    if (bound === action) delete settings.binds[code];
  }
  for (const code of bindRaw(value)) {
    if (!(code in settings.binds)) settings.binds[code] = action;
  }
}

function bindDisplay(value: unknown): string {
  const codes = bindRaw(value);
  return codes.length ? codes.map(displayCode).join(" / ") : "—";
}

const MODE_SETTINGS: SettingDef[] = MODE_GROUPS.flatMap((group) =>
  group.fields.map((field) => {
    const shipped = (DEFAULTS.mode as unknown as Record<string, unknown>)[String(field.key)];
    return {
      id: `mode.${String(field.key)}`,
      category: ["game-modes", slug(group.title)],
      label: field.label,
      kind: field.kind,
      shipped,
      access: "tester" as const,
      path: `mode.${String(field.key)}`,
      min: field.min,
      max: field.max,
      step: field.step,
      unit: field.unit,
      options: field.options,
      help: `${group.title}: ${field.label}. Game-mode rule for the current match.`,
      keywords: ["mode", "game mode", "match", group.title, String(field.key)],
    };
  }),
);

const RULE_SETTINGS: SettingDef[] = RULE_GROUPS.flatMap((group) =>
  group.fields.map((field) => ({
    id: `rules.${field.key}`,
    category: ["rules", slug(group.title)],
    label: field.label,
    kind: "number" as const,
    shipped: DEFAULTS.rules[field.key] ?? field.def,
    access: "tester" as const,
    path: `rules.${field.key}`,
    min: field.min,
    max: field.max,
    step: field.step,
    unit: field.unit,
    help: `${group.title}: ${field.label}. Server-authoritative tuning rule.`,
    keywords: ["rule", "rules", "tuning", "server", group.title, field.key],
  })),
);

const BIND_SETTINGS: SettingDef[] = ACTION_GROUPS.flatMap((group) =>
  group.ids.map((action) => {
    const label = ACTION_LABELS.get(action) ?? action;
    return {
      id: `binds.${action}`,
      category: ["controls", "binds"],
      label: `${label} bind`,
      kind: "bind" as const,
      shipped: bindCodes(DEFAULT_BINDS, action).join(" "),
      access: "player" as const,
      inlineReset: false,
      help: `Key or mouse button bound to ${label}. Edit it on the Controls page.`,
      keywords: ["bind", "binds", "key", "mouse", action, label, group.title],
      get: (settings: unknown) => bindCodes((settings as Settings).binds ?? {}, action).join(" "),
      set: (settings: unknown, value: unknown) => setBindAction(settings as Settings, action, value),
    };
  }),
);

const SOUND_SETTINGS: SettingDef[] = SOUND_EVENTS.map((event) => ({
  id: `sounds.${event.id}`,
  category: ["audio", "sound-events"],
  label: event.label,
  kind: "text" as const,
  shipped: DEFAULTS.sounds[event.id] ?? "",
  access: "player" as const,
  path: `sounds.${event.id}`,
  optionalPath: true,
  help: `Sound file played for ${event.label}.`,
  keywords: ["sound", "sounds", "audio", "event", event.id, event.label],
}));

export const SETTINGS: SettingDef[] = [
  ...CORE_SETTINGS,
  ...MODE_SETTINGS,
  ...RULE_SETTINGS,
  ...BIND_SETTINGS,
  ...SOUND_SETTINGS,
];

/**
 * Short, player-facing explanations. Used for tooltips, the admin editor and
 * the search engine. Keep them one sentence and free of implementation jargon
 * where possible.
 */
const HELP: Record<string, string> = {
  "video.fov": "Horizontal field of view in degrees. Higher values show more of the world but stretch the edges.",
  "video.panini": "0 is plain rectilinear (Quake). Higher values keep verticals straighter at wide FOV by compressing the edges, but magnify the centre. 1 is classic Panini; 0.3–0.5 is a mild blend.",
  "video.textureMode": "Texture filtering quality. Anisotropic is sharpest; Nearest is the retro, aliased look.",
  "video.effects.truelightning": "How closely the Lightning Gun beam follows your crosshair. 1 means the visual beam matches the hit exactly.",
  "video.effects.trailSeconds": "How long the neon player ribbon lasts. 0 turns player trails off.",
  "video.effects.trailWidth": "Half-width of the neon trail ribbon in world units. Higher values make a thicker trail.",
  "video.effects.trailIntensity": "Trail brightness. Higher values feed more glow into bloom.",
  "video.effects.trailSelfMode": "How your own trail colour is chosen: custom, random per life, or your team colour.",
  "video.effects.trailSelfColor": "Your custom trail colour, used when your trail mode is Custom.",
  "video.effects.trailSelfTeamColor": "Your team colour, used when your trail mode is Team.",
  "video.effects.trailEnemyMode": "How enemy trail colours are chosen: one custom colour, random per enemy, or team colour.",
  "video.effects.trailEnemyColor": "Custom colour for all enemies. Also drives the enemy model colour.",
  "video.effects.trailEnemyTeamColor": "Enemy team colour, used when enemy trails are set to Team.",
  "video.enemy.outlineSize": "Size multiplier for the enemy glow outline. 1 is the shipped size.",
  "video.enemy.outlineBrightness": "Brightness of the enemy outline. 0 hides it; values above 1 feed bloom.",
  "video.enemy.haloSize": "Size multiplier for the wider enemy halo shell.",
  "video.enemy.haloBrightness": "Brightness and opacity of the enemy halo. 0 hides it.",
  "video.vfx.explosion": "Colour of explosion fireballs.",
  "video.vfx.sparks": "Colour of impact sparks.",
  "video.vfx.muzzle": "Colour of muzzle flashes.",
  "video.vfx.smoke": "Colour of smoke particles.",
  "video.hit.hitmarker": "Show a short marker on the crosshair when you hit an enemy.",
  "video.hit.hitmarkerStyle": "Shape of the hitmarker: X, cross or dot.",
  "video.hit.hitmarkerSize": "Hitmarker size in pixels.",
  "video.hit.hitmarkerThickness": "Stroke thickness of the hitmarker in pixels.",
  "video.hit.hitmarkerColor": "Hitmarker colour on a normal hit.",
  "video.hit.hitmarkerKillColor": "Hitmarker colour on a killing hit.",
  "video.hit.hitmarkerDuration": "How long the hitmarker stays visible, in milliseconds.",
  "video.hit.hitmarkerOpacity": "Hitmarker opacity. 0.1 is faint; 1 is fully opaque.",
  "video.hit.damageNumbers": "Show floating damage numbers when you deal damage.",
  "video.hit.damageNumberSize": "Damage number text size in pixels.",
  "video.hit.damageNumberColor": "Damage number colour on a normal hit.",
  "video.hit.damageNumberKillColor": "Damage number colour on a killing hit.",
  "video.hit.damageNumberDuration": "How long a damage number stays visible, in milliseconds.",
  "video.hit.damageNumberRise": "Pixels the damage number rises while it fades.",
  "video.hit.damageNumberOpacity": "Damage number opacity.",
  "audio.hitSoundVolume": "Extra volume for the hit sound effect.",
  "audio.volume": "Master volume for all game audio.",
  "controls.sensitivity": "Mouse look sensitivity. Lower values need more mouse movement to turn.",
  "controls.binds": "Keyboard and mouse bindings for every action.",
  "weapons.defaults.yawMultiplier": "Horizontal mouse-turn multiplier for every weapon unless that weapon overrides it.",
  "weapons.defaults.pitchMultiplier": "Vertical mouse-look multiplier for every weapon unless that weapon overrides it.",
  "weapons.settings": "Per-weapon overrides for sensitivity, FOV, yaw/pitch multiplier, crosshair, sounds, effects and position.",
  "weapons.sounds": "Sound file assigned to each game event.",
  "hud.cgaz.opacity": "Overall opacity of the cgaz movement strip.",
  "hud.cgaz.height": "Height of the cgaz strip in pixels.",
  "hud.cgaz.width": "Width of the central cgaz band in pixels.",
  "hud.cgaz.bgOpacity": "Opacity of the dark backing behind the cgaz strip.",
  "hud.cgaz.shadow": "Draw a drop shadow under the cgaz strip.",
  "hud.cgaz.border": "Draw an outline around the cgaz strip.",
  "hud.cgaz.borderWidth": "cgaz outline thickness in pixels.",
  "hud.cgaz.borderColor": "cgaz outline colour.",
  "hud.cgaz.borderOpacity": "cgaz outline opacity.",
  "hud.cgaz.colors.dead": "Colour for the dead or too-slow angle zone.",
  "hud.cgaz.colors.partial": "Colour for the partial strafe-acceleration zone.",
  "hud.cgaz.colors.fast": "Colour for the fast strafe-acceleration zone.",
  "hud.cgaz.colors.turn": "Colour for the turn zone.",
  "hud.cgaz.colors.decel": "Colour for the deceleration zone.",
  "hud.cgaz.alphas.dead": "Opacity for the dead cgaz zone.",
  "hud.cgaz.alphas.partial": "Opacity for the partial cgaz zone.",
  "hud.cgaz.alphas.fast": "Opacity for the fast cgaz zone.",
  "hud.cgaz.alphas.turn": "Opacity for the turn cgaz zone.",
  "hud.cgaz.alphas.decel": "Opacity for the decel cgaz zone.",
  "hud.layout": "Position, scale and visibility of HUD elements.",
  "crosshair.store": "Layered crosshair design and hit effects.",
  "advanced.downstreamHz": "Snapshot rate the client consumes. Lower rates save bandwidth; higher rates update more often.",
  "advanced.debug.wireframe": "Render the world as wireframe. Developer diagnostic only.",
  "advanced.debug.pvs": "Use PVS visibility culling. Off draws every chunk for A/B testing.",
  "advanced.debug.frustum": "Use view-frustum culling on top of PVS.",
  "advanced.debug.rawmouse": "Request raw, unadjusted mouse input instead of the OS pointer curve.",
  "advanced.debug.vfxtrails": "Show glowing and smoke projectile trails.",
  "advanced.debug.vfxcollide": "Let particles collide with and bounce off the world.",
  "advanced.debug.bloom": "Bloom post-process glow around bright VFX.",
  "advanced.debug.netgraph": "Show the network stats graph.",
  "advanced.debug.profiler": "Show the frame-time profiler.",
  "advanced.debug.interp": "How far in the past remote players are rendered. Lower is closer to present but more jittery.",
  "advanced.debug.smooth": "How long server position corrections are smoothed over.",
  "advanced.debug.lag": "Simulated one-way network latency for testing netcode.",
  "advanced.debug.jitter": "Simulated packet jitter for testing netcode.",
  "advanced.debug.loss": "Simulated packet loss for testing netcode.",
  "advanced.debug.fpscap": "Software frame-rate cap. 0 means uncapped or vsync.",
  "advanced.debug.vfxamount": "Multiplier for how many VFX particles spawn.",
  "advanced.debug.smokedensity": "Multiplier for smoke particle count.",
  "advanced.debug.smokeopacity": "How opaque smoke particles are.",
  "advanced.debug.vfxsize": "Particle size multiplier.",
  "advanced.debug.bloomintensity": "How strong the bloom glow is.",
  "advanced.debug.bloomthreshold": "Brightness above which pixels start to bloom.",
};

/**
 * Search synonyms and common abbreviations. The search engine also does
 * token/fuzzy matching, but these make the common names (“fps”, “sens”,
 * “reticle”, …) reliable.
 */
const KEYWORDS: Record<string, string[]> = {
  "video.fov": ["fov", "field of view", "zoom", "view", "wide"],
  "video.panini": ["panini", "rectilinear", "distortion", "fov", "projection", "wide", "cylindrical"],
  "video.textureMode": ["texture", "filter", "filtering", "anisotropic", "trilinear", "bilinear", "nearest", "mipmap", "sharp"],
  "video.effects.truelightning": ["lightning", "lg", "beam", "crosshair", "truelightning"],
  "video.effects.trailSeconds": ["trail", "ribbon", "neon", "duration", "time", "glow"],
  "video.effects.trailWidth": ["trail", "ribbon", "width", "thickness", "neon"],
  "video.effects.trailIntensity": ["trail", "glow", "brightness", "neon", "bloom"],
  "video.effects.trailSelfMode": ["trail", "self", "own", "color", "colour", "mode"],
  "video.effects.trailSelfColor": ["trail", "self", "own", "color", "colour"],
  "video.effects.trailSelfTeamColor": ["trail", "team", "color", "colour"],
  "video.effects.trailEnemyMode": ["trail", "enemy", "color", "colour", "mode"],
  "video.effects.trailEnemyColor": ["trail", "enemy", "color", "colour"],
  "video.effects.trailEnemyTeamColor": ["trail", "enemy", "team", "color", "colour"],
  "video.enemy.outlineSize": ["enemy", "outline", "silhouette", "size"],
  "video.enemy.outlineBrightness": ["enemy", "outline", "brightness", "glow"],
  "video.enemy.haloSize": ["enemy", "halo", "glow", "size"],
  "video.enemy.haloBrightness": ["enemy", "halo", "brightness", "opacity"],
  "video.vfx.explosion": ["vfx", "explosion", "fireball", "color", "colour"],
  "video.vfx.sparks": ["vfx", "sparks", "impact", "color", "colour"],
  "video.vfx.muzzle": ["vfx", "muzzle", "flash", "color", "colour"],
  "video.vfx.smoke": ["vfx", "smoke", "color", "colour"],
  "video.hit.hitmarker": ["hitmarker", "hit", "marker", "feedback"],
  "video.hit.hitmarkerStyle": ["hitmarker", "style", "shape", "cross", "dot"],
  "video.hit.hitmarkerSize": ["hitmarker", "size"],
  "video.hit.hitmarkerThickness": ["hitmarker", "thickness", "stroke"],
  "video.hit.hitmarkerColor": ["hitmarker", "color", "colour", "hit"],
  "video.hit.hitmarkerKillColor": ["hitmarker", "kill", "color", "colour"],
  "video.hit.hitmarkerDuration": ["hitmarker", "duration", "time", "fade"],
  "video.hit.hitmarkerOpacity": ["hitmarker", "opacity", "alpha"],
  "video.hit.damageNumbers": ["damage", "numbers", "floating", "hit"],
  "video.hit.damageNumberSize": ["damage", "numbers", "size"],
  "video.hit.damageNumberColor": ["damage", "numbers", "color", "colour"],
  "video.hit.damageNumberKillColor": ["damage", "numbers", "kill", "color", "colour"],
  "video.hit.damageNumberDuration": ["damage", "numbers", "duration", "time"],
  "video.hit.damageNumberRise": ["damage", "numbers", "rise", "float", "speed"],
  "video.hit.damageNumberOpacity": ["damage", "numbers", "opacity", "alpha"],
  "audio.hitSoundVolume": ["hit", "sound", "volume", "audio"],
  "audio.volume": ["volume", "sound", "audio", "master"],
  "controls.sensitivity": ["sensitivity", "sens", "mouse", "dpi", "cm360", "aim", "look"],
  "controls.binds": ["bind", "binds", "keybind", "keys", "controls", "mouse"],
  "weapons.defaults.yawMultiplier": ["yaw", "pitch", "horizontal", "turn", "mouse", "aim", "sensitivity", "multiplier"],
  "weapons.defaults.pitchMultiplier": ["pitch", "yaw", "vertical", "look", "mouse", "aim", "sensitivity", "multiplier"],
  "weapons.settings": ["weapon", "weapons", "gun", "per weapon", "sensitivity", "fov", "crosshair", "yaw", "pitch"],
  "weapons.sounds": ["sound", "sounds", "audio", "weapon", "event"],
  "hud.cgaz.opacity": ["cgaz", "hud", "opacity", "movement", "strafe"],
  "hud.cgaz.height": ["cgaz", "hud", "height", "movement"],
  "hud.cgaz.width": ["cgaz", "hud", "width", "movement"],
  "hud.cgaz.bgOpacity": ["cgaz", "background", "opacity", "hud"],
  "hud.cgaz.shadow": ["cgaz", "shadow", "hud"],
  "hud.cgaz.border": ["cgaz", "border", "outline", "hud"],
  "hud.cgaz.borderWidth": ["cgaz", "border", "width", "hud"],
  "hud.cgaz.borderColor": ["cgaz", "border", "color", "colour", "hud"],
  "hud.cgaz.borderOpacity": ["cgaz", "border", "opacity", "hud"],
  "hud.cgaz.colors.dead": ["cgaz", "color", "colour", "dead", "slow"],
  "hud.cgaz.colors.partial": ["cgaz", "color", "colour", "partial", "accel"],
  "hud.cgaz.colors.fast": ["cgaz", "color", "colour", "fast", "accel"],
  "hud.cgaz.colors.turn": ["cgaz", "color", "colour", "turn"],
  "hud.cgaz.colors.decel": ["cgaz", "color", "colour", "decel"],
  "hud.cgaz.alphas.dead": ["cgaz", "alpha", "opacity", "dead"],
  "hud.cgaz.alphas.partial": ["cgaz", "alpha", "opacity", "partial"],
  "hud.cgaz.alphas.fast": ["cgaz", "alpha", "opacity", "fast"],
  "hud.cgaz.alphas.turn": ["cgaz", "alpha", "opacity", "turn"],
  "hud.cgaz.alphas.decel": ["cgaz", "alpha", "opacity", "decel"],
  "hud.layout": ["hud", "layout", "position", "scale", "move", "hide"],
  "crosshair.store": ["crosshair", "reticle", "aim", "layers", "hit effect"],
  "advanced.downstreamHz": ["snapshot", "rate", "downstream", "network", "hz", "bandwidth"],
  "advanced.debug.wireframe": ["wireframe", "debug", "developer", "render"],
  "advanced.debug.pvs": ["pvs", "culling", "visibility", "debug"],
  "advanced.debug.frustum": ["frustum", "culling", "debug"],
  "advanced.debug.rawmouse": ["raw", "mouse", "input", "acceleration"],
  "advanced.debug.vfxtrails": ["vfx", "trail", "projectile", "particles"],
  "advanced.debug.vfxcollide": ["vfx", "collide", "particles", "bounce"],
  "advanced.debug.bloom": ["bloom", "glow", "post process"],
  "advanced.debug.netgraph": ["netgraph", "network", "ping", "graph"],
  "advanced.debug.profiler": ["profiler", "fps", "frame time", "performance"],
  "advanced.debug.interp": ["interpolation", "interp", "delay", "network", "smooth"],
  "advanced.debug.smooth": ["smoothing", "correction", "network", "reconcile"],
  "advanced.debug.lag": ["latency", "lag", "network", "simulate", "test"],
  "advanced.debug.jitter": ["jitter", "network", "simulate", "test"],
  "advanced.debug.loss": ["loss", "packet loss", "network", "simulate", "test"],
  "advanced.debug.fpscap": ["fps", "frame rate", "cap", "limit", "vsync"],
  "advanced.debug.vfxamount": ["vfx", "particles", "amount", "density"],
  "advanced.debug.smokedensity": ["smoke", "density", "particles"],
  "advanced.debug.smokeopacity": ["smoke", "opacity", "alpha"],
  "advanced.debug.vfxsize": ["vfx", "particle", "size"],
  "advanced.debug.bloomintensity": ["bloom", "intensity", "glow"],
  "advanced.debug.bloomthreshold": ["bloom", "threshold", "glow"],
};

for (const def of SETTINGS) {
  def.help ??= HELP[def.id];
  def.keywords = KEYWORDS[def.id] ?? [];
}

export function getSettingValue(def: SettingDef, settings: unknown): unknown {
  if (def.get) return def.get(settings);
  if (def.path) return getPath(settings, def.path);
  return def.shipped;
}

export function setSettingValue(def: SettingDef, settings: unknown, value: unknown): void {
  if (def.set) {
    def.set(settings, value);
    return;
  }
  if (def.path) setPath(settings, def.path, value);
}

export function formatSettingValue(def: SettingDef, value: unknown): string {
  if (def.kind === "bind") return bindDisplay(value);
  if (def.kind === "toggle") return value ? "on" : "off";
  if (def.kind === "number") {
    if (value === undefined || value === null) return "—";
    return `${value}${def.unit ? ` ${def.unit}` : ""}`;
  }
  if (def.kind === "select") {
    return def.options?.find((o) => o.value === String(value))?.label ?? String(value ?? "—");
  }
  if (def.kind === "group") return "shipped";
  const text = String(value ?? "");
  return text || "—";
}

export function formatDefault(def: SettingDef): string {
  return formatSettingValue(def, def.shipped);
}

export const SETTINGS_BY_ID = new Map(SETTINGS.map((def) => [def.id, def]));

/**
 * Dev-only registry sanity check. Diagnostics only: it never changes values.
 * Catches duplicate ids and paths that drift away from the shipped defaults.
 */
export function validateSettingsRegistry(): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const def of SETTINGS) {
    if (ids.has(def.id)) errors.push(`duplicate setting id: ${def.id}`);
    ids.add(def.id);
    if (!def.category.length) errors.push(`missing category: ${def.id}`);
    if (!def.help?.trim()) errors.push(`missing help/explanation: ${def.id}`);
    if (def.path) {
      const value = getPath(DEFAULTS, def.path);
      if (value === undefined && !def.optionalPath) {
        errors.push(`missing Settings path: ${def.id} -> ${def.path}`);
      } else if (value !== undefined && def.kind !== "group" && typeof value !== typeof def.shipped) {
        errors.push(
          `shipped type mismatch: ${def.id} (${typeof value} in Settings, ${typeof def.shipped} shipped)`,
        );
      }
    }
  }
  return errors;
}

export const CATEGORY_LABELS: Record<string, string> = {
  video: "Video",
  "video.effects": "Effects",
  "video.hit-feedback": "Hit feedback",
  audio: "Audio",
  "audio.sound-events": "Sound events",
  controls: "Controls",
  "controls.binds": "Key binds",
  weapons: "Weapons",
  hud: "HUD",
  "game-modes": "Game modes",
  rules: "Rules & tuning",
  advanced: "Advanced",
  "advanced.debug": "Debug / runtime",
};

function titleCase(text: string): string {
  return text
    .split(/[-\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function categoryLabel(path: string[]): string {
  return path
    .map((part, i) => CATEGORY_LABELS[path.slice(0, i + 1).join(".")] ?? titleCase(part))
    .join(" / ");
}

/**
 * Page that owns the live control for a setting. Most follow their category,
 * but a few live on a different page than their category suggests.
 */
export function pageForSetting(def: SettingDef): string {
  if (def.id === "controls.sensitivity" || def.id.startsWith("weapons.defaults.")) return "weapons";
  if (def.id === "audio.hitSoundVolume") return "video";
  if (def.id === "weapons.sounds" || def.id.startsWith("sounds.")) return "audio";
  if (def.id.startsWith("binds.") || def.id === "controls.binds") return "controls";
  if (def.id.startsWith("hud.cgaz.")) return "settings";
  if (def.id === "hud.layout" || def.id === "crosshair.store") return "hud";
  switch (def.category[0]) {
    case "game-modes":
      return "modes";
    case "rules":
      return "rules";
    case "video":
      return "video";
    case "audio":
      return "audio";
    case "controls":
      return "controls";
    case "weapons":
      return "weapons";
    case "hud":
      return "hud";
    case "advanced":
      return "advanced";
    default:
      return "settings";
  }
}

/** Read a dot path from a plain object. */
export function getPath(obj: unknown, path: string): unknown {
  let cur: unknown = obj;
  for (const part of path.split(".")) {
    if (!cur || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

/** Write a dot path into a plain object. */
export function setPath(obj: unknown, path: string, value: unknown): void {
  const parts = path.split(".");
  let cur: unknown = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    if (!cur || typeof cur !== "object") return;
    cur = (cur as Record<string, unknown>)[parts[i]];
  }
  if (cur && typeof cur === "object") {
    (cur as Record<string, unknown>)[parts[parts.length - 1]] = value;
  }
}
